import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { queryKeys } from '../lib/queryKeys.js';
import { useSession } from '../stores/session.js';
import { toast } from '../stores/ui.js';

/**
 * Establishing and ending an admin session.
 *
 * The permission list comes back with the session on every read, so a role
 * changed by an administrator takes effect on this operator's next window focus
 * rather than at their next sign-in.
 */

/**
 * Puts the panel into the signed-out state in one step: the store says guest,
 * every cached screen is dropped, and the session query is settled as "nobody".
 *
 * Settled, not removed. This used to be `client.clear()`, which also deleted
 * the session query — often while that query was the very request that had
 * just failed. The shell, still mounted and watching it, then started a new
 * session read, which failed the same way and cleared the cache again, and
 * the panel sat on "Checking your session" instead of reaching the sign-in
 * screen. That happened on every signed-out visit and on every sign-out.
 * Leaving the session answered as `null` gives the shell nothing to fetch, so
 * it redirects.
 */
export const resetToSignedOut = (client) => {
  useSession.getState().signedOut();
  client.removeQueries();
  client.setQueryData(queryKeys.session(), null);
};
export const useEstablishSession = () => {
  const signedIn = useSession((state) => state.signedIn);
  const signedOut = useSession((state) => state.signedOut);

  return useQuery({
    queryKey: queryKeys.session(),
    queryFn: async () => {
      try {
        const result = await api.get('/admin/auth/me');
        signedIn(result.admin, result.permissions);
        return result;
      } catch {
        signedOut();
        return null;
      }
    },
    retry: false,
    staleTime: 60 * 1000,
  });
};

export const useLogin = () => {
  const client = useQueryClient();
  const signedIn = useSession((state) => state.signedIn);

  return useMutation({
    mutationFn: (credentials) => api.post('/admin/auth/login', credentials),
    onSuccess: async (result) => {
      // The login response carries the account but not the expanded permission
      // set, so the session read follows immediately rather than guessing.
      const session = await api.get('/admin/auth/me');
      signedIn(session.admin, session.permissions);
      client.setQueryData(queryKeys.session(), session);
      return result;
    },
  });
};

/**
 * The server clears the cookies whether or not the access token is still
 * valid, so the panel signs out locally even when the request fails — a
 * sign-out button that does nothing on a flaky connection is worse than one
 * that needs pressing again on the next visit.
 */
export const useLogout = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => api.post('/admin/auth/logout'),
    onSuccess: () => toast({ title: 'Signed out' }),
    onSettled: () => resetToSignedOut(client),
  });
};

export const useChangePassword = () =>
  useMutation({
    mutationFn: (values) => api.post('/admin/auth/password', values),
  });
